import { PunkGameConfig } from "../../../PunkGameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import { TimelineMax, TweenLite } from "gsap";
import { Point, Sprite, BLEND_MODES } from "pixi.js";
import { ReelItemMap, IItemTransform, IReelItem } from "../../../punkEngine/mapping/ReelItemMap";
import { SymbolMultiplyCounter } from "../symbol/SymbolMultiplyCounter";
import { ReelState } from "@nolimitcity/slot-game/bin/core/engines/reel/ReelEngineController";
import { StateHandler } from "@nolimitcity/slot-game/bin/core/utils/StateHandler";
import { ReelAnimationManager } from "../reelAnimator/ReelAnimationManager";
import { StageManager } from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import { Sequence } from "@nolimitcity/slot-game/bin/core/displayobject/Sequence";
import { PunkEngineManager } from "../../../punkEngine/PunkEngineManager";
import { Helper } from "@nolimitcity/slot-game/bin/core/utils/Helper";
import { MathHelper } from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import { PunkEngineController } from "../../../punkEngine/PunkEngineController";
import { PunkGameModuleConfig } from "../../../PunkGameModuleConfig";

interface WildItem extends IReelItem {
    ID:number;
    cont: PIXI.Container;
    sprite:Sprite;
    spine:PIXI.spine.Spine;
    state:string;
    merged:boolean;
    mult: SymbolMultiplyCounter;
    timeline?: TimelineMax;
    seq: Sequence;
    jumped:boolean;
}

interface WildAnimationJump {
    ID: number;
    state:string;
    mult?: number;
    to: { reel: number, row: number },
    duration?: number;
}

export class JumpWildState extends StateHandler{

    public static readonly INACTIVE:string = "inactive";
    public static readonly IDLE:string = "Jumping_Idle";
    public static readonly MERGE:string = "Jumping_Merge";
    public static readonly MERGE_IDLE:string = "Jumping_Merge_Idle";
    public static readonly MERGE_WIN:string = "Jumping_Merge_Win";
    public static readonly SPLIT:string = "Jumping_Split";
    public static readonly WIN:string = "Jumping_Win";
    
    public SpineStateDuration(state:string):number{
        switch(state){
            case JumpWildState.IDLE:
                return MathHelper.frameToSeconds(15);
            case JumpWildState.MERGE:
                return MathHelper.frameToSeconds(30);
            case JumpWildState.MERGE_IDLE:
                return MathHelper.frameToSeconds(30);
            case JumpWildState.MERGE_WIN:
                return MathHelper.frameToSeconds(30);
            case JumpWildState.SPLIT:
                return MathHelper.frameToSeconds(30);
            case JumpWildState.WIN:
                return MathHelper.frameToSeconds(30);
            default:
                return MathHelper.frameToSeconds(0);
        }
    }

    public get isIdle():boolean{
        return this.state === JumpWildState.IDLE || this.state === JumpWildState.MERGE_IDLE;
    }

    public SpineLoops(state:string):boolean{
        switch(state){
            case JumpWildState.IDLE:
            case JumpWildState.MERGE_IDLE:
                return true;
            default:
                return false;
        }
    }

    public NextSpineState(state:string):string{
        switch(state){
            case JumpWildState.MERGE:
            case JumpWildState.MERGE_WIN:
                return JumpWildState.MERGE_IDLE;
            case JumpWildState.SPLIT:
            case JumpWildState.WIN:
                return JumpWildState.IDLE;
            default:
                return state;
        }
    }
}

export class JumpingWildController extends ReelItemMap {

    private static _instance: JumpingWildController;
    protected readonly _state:JumpWildState = new JumpWildState(JumpWildState.IDLE);
    protected readonly animationSizeY: number = 148;
    private readonly _skullSpritePath:string = "Wild/Skull_Reel";

    public static get instance(): JumpingWildController {
        return this._instance;
    }

    public get items():WildItem[]{
        return this._items as WildItem[];
    }

    constructor() {
        super();
        if (!JumpingWildController._instance) {
            JumpingWildController._instance = this;
            StageManager.getLayer(PunkGameConfig.instance.LAYERS.STICKY.name).addChild(this);
        }
    }

    public changeMultText(ID: number, text: string): void {
        const item: WildItem = this.getExistingItem(ID)!;
        item.mult.zIndex = parseFloat(text.split("X")[1])*100;
        item.mult.text = text.toLowerCase();
        this.sortChildren();
    }

    public changeMult(ID: number, multValue: number): void {
        const item: WildItem = this.getExistingItem(ID)!;
        if(multValue.toString() != "1"){
            item.mult.text = multValue.toString()+ "X";
            item.mult.zIndex = multValue*100;
            this.sortChildren();
        }else if(multValue.toString() == "1" && item.mult.text.length > 0){
            item.mult.text = "";
        }
    }

    public itemExists(reelID:number, rowID:number):number{
        return !!super.getExistingItem(reelID,rowID) ? (super.getExistingItem(reelID,rowID) as WildItem).ID : -1;
    }

    public playJumpAttentionHit(ID: number):TimelineMax{
        const item: WildItem = this.getExistingItem(ID)!;
        return item.seq.playAnimation({onCompleteCallback:() => item.seq.alpha = 0, onStart:() => item.seq.alpha = 1});
    }

    public PlayState(ID: number, state:string, autoNextSpine:boolean = true): TimelineMax {
        const item: WildItem = this.getExistingItem(ID)!;
        
        item.spine.visible = state != JumpWildState.INACTIVE;
        if(!item.spine.visible) return new TimelineMax();
        if(!item.merged)item.merged = state === JumpWildState.MERGE;

        if(item.merged && JumpWildState.WIN == state) state = JumpWildState.MERGE_WIN;
        if(item.merged && JumpWildState.IDLE == state) state = JumpWildState.MERGE_IDLE;
            
        item.timeline = new TimelineMax({
            onComplete:() => (autoNextSpine ? item.spine.state.setAnimation(0, this._state.NextSpineState(state), true) : {})
        });

        const durr:number = this._state.SpineStateDuration(state);
        const loops:boolean = this._state.SpineLoops(state);
            
        item.timeline.add([
            () => item.spine.state.setAnimation(0, state, loops),
            TweenLite.to(this,durr,{})
        ],0)

        item.timeline.play();
        return item.timeline;
    }

    public JumpTo(conf: WildAnimationJump): TimelineMax {
        const item: WildItem = this.getExistingItem(conf.ID)!;
        const fromPosition: Point = item.cont.position as Point;

        const reelC:PunkEngineController = PunkEngineManager.getReel(conf.to.reel);
        
        const toPosition: Point = reelC.getCurrentStatePositionMap(item.cont)[conf.to.row + reelC.overflowDirection];

        item.timeline = new TimelineMax({
            onStart:() => {
                item.jumped = false;
                item.merged = false;  
            },
            onComplete: () => {
                item.jumped = true;
                item.ReelID = conf.to.reel;
                item.SymID = conf.to.row;
                item.spine.state.setAnimation(0, this._state.NextSpineState(conf.state), true);
                this.updateItemTransform(item,true);
            },
        });

        const duration:number = conf.duration ? conf.duration : this._state.SpineStateDuration(conf.state);
        const loops:boolean = this._state.SpineLoops(conf.state);

        item.timeline.add([
            TweenLite.fromTo(item.cont, duration,
                { x: fromPosition.x, y: fromPosition.y },
                { x: toPosition.x, y: toPosition.y }),
                () => item.spine.state.setAnimation(0, conf.state, loops)
        ],0)
        item.timeline.play();
        return item.timeline;
    }

    public updatePosID(itemID:number, reelID:number,symID:number){
        const item: WildItem = this.getExistingItem(itemID)!;
        item.ReelID = reelID;
        item.SymID = symID;
    }

    public updateSymbolPositions(item:WildItem, duration:number = 0):void{
        if(duration == 0){
            this.updateItemTransform(item,false);
        }else{
            item.timeline = new TimelineMax({ onUpdate:() => { this.playingTimeline(item,false); }});
            item.timeline.to(item,duration,{});
            item.timeline.play();
        }
    }

    public createJumpWildsFS(reelID: number, symID: number): void {
        const item: WildItem = this.getItem(this.children.length, reelID, symID);
        const trainItem:Sprite = PunkGameModuleConfig.xWayCounter.getTrainItem(reelID);
        item.cont.position = Helper.copyLocalPosition(item.cont , trainItem);
    }

    public createJumpWilds(reelID: number, symID: number): void {
        const item: WildItem = this.getItem(this.children.length, reelID, symID);
        const skull:Sprite =  ReelAnimationManager.bonusReel.ActiveSkulls[this.children.length-1];
        item.cont.position = Helper.copyLocalPosition(item.cont , skull);
    }

    public StartShow(): void {
        for(let i:number = 0 ; i < this.children.length;i++){
            const item: WildItem = this.getExistingItem(i)!;
            item.sprite.alpha = 1;
        }
    }

    public ChangeToSpine():void{
        for(let i:number = 0 ;i < this.children.length;i++){
            const item: WildItem = this.getExistingItem(i)!;
            if (!item.spine.visible) {
                item.timeline = new TimelineMax({onStart:() => {
                    item.mult.visible = true;
                    item.spine.visible = true;
                    item.spine.state.setAnimation(0, JumpWildState.IDLE, true)
                }});
                item.timeline.to(item.sprite,0.25,{alpha:0}, 0);
                item.timeline.fromTo(item.spine,0.25,{alpha:0},{alpha:1}, 0);
            }
        }
    }

    public HideSkulls(duration:number = 0.3): void {
        const tl:TimelineMax = new TimelineMax({onComplete:() => this.cleanItems()})
        for(let i:number =0 ;i < this.children.length;i++){
            const item: WildItem = this.getExistingItem(i)!;
            tl.to(item.cont,duration,{alpha:0},0);
        }
        tl.play();
    }

    // Functions
    protected updateItemTransform(item: WildItem, useMap:boolean = true): void {
        if (item == undefined) return;
        if(!item.jumped) return;
        if(item.timeline && item.timeline.isActive()) return;
        
        const isIdle:boolean = PunkEngineManager.getReel(item.ReelID).state.isIdle || PunkEngineManager.getReel(item.ReelID).state.equal(ReelState.EXPAND_SYM);
        const transformData: IItemTransform = this.getSymbolTransformData(item, item.cont, !isIdle ,  ReelState.STOP)!;
        item.cont.position = transformData.position;
        item.cont.scale = transformData.scale;
    }

    protected playingTimeline(item: WildItem, useMap:boolean = true): void {
        const isIdle:boolean = PunkEngineManager.getReel(item.ReelID).state.isIdle || PunkEngineManager.getReel(item.ReelID).state.equal(ReelState.EXPAND_SYM);
        const transformData: IItemTransform = this.getSymbolTransformData(item, item.cont, !isIdle ,  ReelState.STOP)!;
        item.cont.position = transformData.position;
        item.cont.scale = transformData.scale;
    }

    public getExistingItem(ID:number):WildItem | undefined{
        for(let id:number = 0; id < this._items.length; id++){
            if(this._items[id] && (this._items[id] as WildItem).ID == ID) return this._items[id] as WildItem;
        }
        return undefined;
    }

    protected getItem(ID:number, reelID: number, symID: number): WildItem {
        let item: WildItem | undefined = this.getExistingItem(ID) as WildItem;
        if (item) return item;

        const mult: SymbolMultiplyCounter = new SymbolMultiplyCounter();
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset("JWild"));
        const sprite: Sprite = Helper.createSprite(GameResources.getTextures(this._skullSpritePath)[0]);
        const seq:Sequence = Sequence.createSequence(GameResources.getTextures("JumpFire"))

        item =  <WildItem>super.createItem(reelID, symID, {seq: seq , ID:this.children.length, spine: spine, sprite:sprite, mult: mult, cont: new PIXI.Container(), jumped:false } as WildItem );
        item.cont.zIndex = this.children.length;

        spine.visible = false;
        mult.visible = false;
        mult.pivot.set(0, 70);
        mult.scale.set(.8, .8);
        mult.text = "";

        seq.scale.set(2,2);
        seq.blendMode = BLEND_MODES.ADD;

        item.cont.addChild(spine);
        item.cont.addChild(mult);
        item.cont.addChild(sprite);
        item.cont.addChild(seq);
        this._items.push(item);
        this.addChild(item.cont);
        this.updateItemTransform(item);
        this.sortChildren();
        return item;
    }

    public cleanItems(): void { super.reset(); }

    protected onResize():void{}
}